/* ============================================================
   SaveAura — UI motion & interactions (no framework, no Shopify dep)
   Loaded by both the Shopify theme and the standalone preview.
   Everything degrades gracefully under prefers-reduced-motion.
   ============================================================ */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // In the Shopify theme editor, sections re-render via AJAX without re-running
  // this script — so anything hidden for scroll-reveal would stay blank.
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function revealAll(scope) {
    (scope || document).querySelectorAll("[data-reveal]").forEach(function (el) {
      el.classList.add("is-visible");
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    var els = document.querySelectorAll("[data-reveal]");
    if (!els.length) return;

    if (designMode || reduceMotion || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });

    els.forEach(function (el, i) {
      // auto-stagger siblings that share a [data-reveal-group] parent
      if (el.closest("[data-reveal-group]") && !el.style.getPropertyValue("--reveal-delay")) {
        var siblings = Array.prototype.slice.call(
          el.closest("[data-reveal-group]").querySelectorAll("[data-reveal]")
        );
        el.style.setProperty("--reveal-delay", (siblings.indexOf(el) * 80) + "ms");
      }
      io.observe(el);
    });
  }

  /* ---------- Header solidify on scroll ---------- */
  function initHeader() {
    var header = document.querySelector("[data-header]");
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle("is-stuck", window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- Hero intro ---------- */
  function initHero() {
    var hero = document.querySelector("[data-hero]");
    if (!hero) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { hero.classList.add("is-ready"); });
    });
  }

  /* ---------- Marquee: duplicate track for seamless loop ---------- */
  function initMarquee() {
    document.querySelectorAll("[data-marquee]").forEach(function (track) {
      if (track.dataset.cloned) return;
      track.dataset.cloned = "true";
      track.setAttribute("aria-hidden", "false");
      var clone = track.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      track.parentNode.appendChild(clone);
    });
  }

  /* ---------- Generic open/close panels (drawer, menu, overlay) ---------- */
  var openPanels = [];
  function openPanel(el) {
    if (!el) return;
    el.classList.add("is-open");
    el.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    openPanels.push(el);
  }
  function closePanel(el) {
    if (!el) return;
    el.classList.remove("is-open");
    el.setAttribute("aria-hidden", "true");
    openPanels = openPanels.filter(function (p) { return p !== el; });
    if (!openPanels.length) document.body.style.overflow = "";
  }
  function closeAll() { openPanels.slice().forEach(closePanel); }

  function initPanels() {
    var overlay = document.querySelector("[data-overlay]");

    // open triggers
    document.querySelectorAll("[data-open]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var target = document.querySelector(btn.getAttribute("data-open"));
        openPanel(target);
        if (overlay) openPanel(overlay);
        var focusable = target && target.querySelector("button, a, input");
        if (focusable) focusable.focus();
      });
    });

    // close triggers
    document.querySelectorAll("[data-close]").forEach(function (btn) {
      btn.addEventListener("click", closeAll);
    });
    if (overlay) overlay.addEventListener("click", closeAll);

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeAll();
    });
  }
  // expose for cart.js
  window.SaveAura = window.SaveAura || {};
  window.SaveAura.openPanel = openPanel;
  window.SaveAura.closePanel = closePanel;
  window.SaveAura.closeAll = closeAll;

  /* ---------- Accordions ---------- */
  function initAccordions() {
    document.querySelectorAll("[data-accordion-btn]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var panel = btn.nextElementSibling;
        var expanded = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", String(!expanded));
        panel.style.maxHeight = expanded ? null : panel.scrollHeight + "px";
      });
    });
  }

  /* ---------- Product gallery thumbnails ---------- */
  function initGallery() {
    var main = document.querySelector("[data-gallery-main]");
    if (!main) return;
    document.querySelectorAll("[data-gallery-thumb]").forEach(function (thumb) {
      thumb.addEventListener("click", function () {
        main.src = thumb.dataset.full || thumb.src;
        document.querySelectorAll("[data-gallery-thumb]").forEach(function (t) {
          t.classList.remove("is-active");
        });
        thumb.classList.add("is-active");
      });
    });
  }

  /* ---------- Quantity steppers (UI only) ---------- */
  function initQty() {
    document.querySelectorAll("[data-qty]").forEach(function (wrap) {
      var input = wrap.querySelector("input");
      wrap.querySelectorAll("[data-step]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var step = parseInt(btn.dataset.step, 10);
          var val = Math.max(1, (parseInt(input.value, 10) || 1) + step);
          input.value = val;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    });
  }

  /* ---------- Toast helper ---------- */
  var toastTimer;
  function toast(msg) {
    var el = document.querySelector("[data-toast]");
    if (!el) return;
    el.querySelector("[data-toast-msg]").textContent = msg;
    el.classList.add("is-open");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove("is-open"); }, 2600);
  }
  window.SaveAura.toast = toast;

  /* ---------- Init ---------- */
  function init() {
    initReveal();
    initHeader();
    initHero();
    initMarquee();
    initPanels();
    initAccordions();
    initGallery();
    initQty();

    /* Shopify theme editor: re-initialise sections that load/reload via AJAX
       so edited sections never appear blank and stay interactive. */
    document.addEventListener("shopify:section:load", function (e) {
      var scope = e.target || document;
      revealAll(scope);
      initHeader();
      initHero();
      initMarquee();
      initAccordions();
      initGallery();
      initQty();
    });
    document.addEventListener("shopify:section:select", function (e) { revealAll(e.target); });
    document.addEventListener("shopify:block:select", function (e) { revealAll(e.target); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
